import axios, { AxiosInstance } from 'axios';
import type {
  SendMessageRequest,
  SendMessageResponse,
  GetHistoryResponse,
  CreateSessionRequest,
  CreateSessionResponse,
} from '@/types/chat';

export class ChatApiService {
  private client: AxiosInstance;
  private apiKey: string;

  constructor(apiEndpoint: string, apiKey: string) {
    this.apiKey = apiKey;
    this.client = axios.create({
      baseURL: apiEndpoint,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey,
      },
      timeout: 120000, // 2 minutes for AI operations with visualizations
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          // Server responded with error
          const message = error.response.data?.message || error.message;
          throw new Error(message);
        } else if (error.request) {
          // Request made but no response
          throw new Error('No response from server. Please check your connection.');
        } else {
          // Error setting up request
          throw new Error(error.message);
        }
      }
    );
  }

  async createSession(request: CreateSessionRequest): Promise<CreateSessionResponse> {
    const response = await this.client.post<CreateSessionResponse>('/chat/session', request);
    return response.data;
  }

  async sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
    const response = await this.client.post<SendMessageResponse>('/chat/message', request);
    return response.data;
  }

  async sendMessageWithWorkflow(workflowId: string, sessionId: string, message: string, userId?: string, metadata?: Record<string, any>): Promise<SendMessageResponse> {
    const response = await this.client.post<SendMessageResponse>('/chat/message', {
      workflowId,
      sessionId,
      message,
      userId,
      metadata,
    });
    return response.data;
  }

  async getHistory(sessionId: string, cursor?: string): Promise<GetHistoryResponse> {
    const response = await this.client.get<GetHistoryResponse>(`/chat/history/${sessionId}`, {
      params: { cursor },
    });
    return response.data;
  }

  async deleteSession(sessionId: string): Promise<void> {
    await this.client.delete(`/chat/session/${sessionId}`);
  }
}
